/**
 * Proof-of-Work solver for the Mirlo Webchat Widget.
 *
 * The backend may require a PoW solution before issuing a session token.
 * Flow:
 *   1. GET  /api/v1/webchat/challenge → { challenge, signed, issued_at, difficulty, ttl_seconds }
 *   2. Find nonce N such that sha256(`${challenge}:${N}`) starts with `difficulty` hex zeroes
 *   3. Submit { challenge, signed, issued_at, nonce } in the handshake body
 *
 * We use js-sha256 (synchronous, ~2 KB gzipped) instead of crypto.subtle because
 * the solver loops up to ~65 k+ iterations — async/await overhead per iteration
 * would make it unbearably slow.
 */

import { sha256 } from 'js-sha256';
import type { ProofOfWorkSolution } from './api-types';

interface ChallengeResponse {
  challenge: string;
  signed: string;
  issued_at: number;
  difficulty: number;
  ttl_seconds: number;
}

export async function fetchChallenge(
  apiBaseUrl: string,
): Promise<ChallengeResponse> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/api/v1/webchat/challenge`);
  } catch (err) {
    throw new Error(
      `PoW challenge fetch failed: ${(err as Error).message || 'network_error'}`,
    );
  }

  if (!response.ok) {
    throw new Error(`PoW challenge HTTP ${response.status}`);
  }

  return (await response.json()) as ChallengeResponse;
}

/**
 * Find the nonce that satisfies the PoW puzzle synchronously.
 * Returns the nonce as a decimal string.
 */
export function solveChallenge(challenge: string, difficulty: number): string {
  const prefix = '0'.repeat(difficulty);
  let nonce = 0;
  while (true) {
    const hash = sha256(`${challenge}:${nonce}`);
    if (hash.startsWith(prefix)) {
      return String(nonce);
    }
    nonce++;
  }
}

/**
 * High-level helper: fetch the challenge from the backend, solve it, and
 * return a complete ProofOfWorkSolution ready to attach to the handshake.
 */
export async function obtainProofOfWork(
  apiBaseUrl: string,
): Promise<ProofOfWorkSolution> {
  const { challenge, signed, issued_at, difficulty } =
    await fetchChallenge(apiBaseUrl);
  const nonce = solveChallenge(challenge, difficulty);
  return { challenge, signed, issued_at, nonce };
}
